import React, { useContext, useState } from 'react'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Descriptions, Modal, Result } from 'antd'
import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import CopyToClipboard from 'react-copy-to-clipboard'
import moment from 'moment/moment'
import { GetWordpressDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'

const WordpressIntegration = (props: IntegrationProps<GetWordpressDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const svc = useService<DomainIntegrationService>(DomainIntegrationService)

    const createIntegration = async () => {
        if (loading || mode !== 'add') return
        setPrimaryActionLoading(true)

        Modal.destroyAll()

        const modal = Modal.success({
            className: 'platform-integration-modal completing-oauth-dialog',
            title: 'Creating WordPress API Key',
            icon: <span className="wordpress-logo-color" />,
            okText: 'Complete',
            cancelText: 'Cancel',
            cancelButtonProps: {
                className: 'platform-integration-modal-action-cancel',
                type: 'text',
            },
            footer: null,
            content: (
                <div>
                    <Loading3QuartersOutlined spin={true} />
                </div>
            ),
        } as any)

        const res = await svc.completeIntegrationSetup(
            domain.id,
            DomainIntegrationProviders.WORDPRESS,
            {},
            {
                cancellationKey: 'wordpress-integration',
            },
        )

        setPrimaryActionLoading(false)

        const wpApiToken = res.data?.data?.token
        if (!res.ok || !wpApiToken) {
            modal.update({
                className: 'platform-integration-modal failed-oauth-dialog',
                title: 'Wordpress API Key Creation Failed',
                content: (
                    <Result
                        status="warning"
                        title={false}
                        subTitle="An error occurred while attempting to create the Wordpress API Key."
                    />
                ),
                onOk: () => {
                    modal.destroy()
                },
            })

            return
        }

        modal.update({
            className: 'platform-integration-modal wordpress-api-key-success-modal',
            title: 'WordPress API Key Created',
            content: (
                <div className="api-key-success-modal-content">
                    <div>
                        <span>
                            The following API Key should be saved on the WordPress plugin Settings page in the{' '}
                            <b>API Key</b> text box. Click the key to copy it your clipboard.
                        </span>
                    </div>
                    <div className="api-key-viewable">
                        <CopyToClipboard
                            text={wpApiToken}
                            onCopy={() =>
                                simpleNotification('success', 'WordPress API Key has been copied to your clipboard')
                            }
                        >
                            <span>{wpApiToken}</span>
                        </CopyToClipboard>
                    </div>
                    <div>
                        <span>
                            Once this window is closed you will not be able to access this key again. In the event of
                            the key being lost, a new key must be created at which point this key will no longer be
                            valid.
                        </span>
                    </div>
                </div>
            ),
            okText: 'Close',
            onOk: () => {
                modal.destroy()
                dispatch({
                    type: 'add-integration',
                    integration: res.data!,
                })
            },
        })
    }

    const removeIntegration = async () => {
        if (integration) {
            setPrimaryActionLoading(true)

            Modal.confirm({
                className: 'platform-integration-modal remove-integration-dialog',
                title: 'Invalidate WordPress API Key',
                icon: <span className="wordpress-logo-color" />,
                okText: 'Complete',
                okButtonProps: {
                    className: 'platform-integration-modal-action-ok action-type-remove',
                    type: 'text',
                },
                cancelText: 'Cancel',
                cancelButtonProps: {
                    className: 'platform-integration-modal-action-cancel',
                    type: 'text',
                },
                content: (
                    <div>
                        <p>
                            Performing this step will invalidate the WordPress API Key attached to this domain resulting
                            in notifications no longer being sent when articles are published.
                        </p>
                        <p>
                            In order to disable prompting visitors to subscribe to push when they visit your site you
                            will also need to navigate to your WordPress Admin and uninstall the plugin.
                        </p>
                    </div>
                ),
                onOk: async () => {
                    const { ok } = await svc.removeIntegration(integration.domain_id, integration.id)
                    setPrimaryActionLoading(true)

                    if (ok) {
                        simpleNotification('success', 'Wordpress API Key was successfully invalidated.')
                        dispatch({ type: 'rem-integration', integration })
                    }
                },
                onCancel: async () => {
                    setPrimaryActionLoading(false)
                },
            })
        }
    }

    return (
        <PlatformIntegrationCard
            loading={loading}
            provider={DomainIntegrationProviders.WORDPRESS}
            logo={<span className="wordpress-logo-color" />}
            mode={integration?.id ? 'edit' : 'add'}
            isActive={integration?.is_active}
            actions={{
                primary: {
                    loading: primaryActionLoading,
                    title: integration?.id ? 'Invalidate API Key' : 'New API Key',
                    onClick: () => {
                        if (!integration) {
                            createIntegration()
                        } else {
                            removeIntegration()
                        }
                    },
                },
            }}
        >
            {integration && (
                <Descriptions layout="horizontal" column={1}>
                    <Descriptions.Item label="Status">Enabled</Descriptions.Item>
                    <Descriptions.Item label="Last Active">
                        {integration.data.token_last_used_at
                            ? moment(integration.data.token_last_used_at).format('LL')
                            : 'Never'}
                    </Descriptions.Item>
                </Descriptions>
            )}
        </PlatformIntegrationCard>
    )
}

export default WordpressIntegration
